import React, { useState, useEffect } from 'react';
import { 
  Monitor, Download, Key, CheckCircle, XCircle, 
  RefreshCw, Zap, Shield, MousePointer, FileSearch,
  Globe, Terminal, Clock, CreditCard, Gift
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AgentDesktopPage() {
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);
  const [devices, setDevices] = useState([]);
  const [token, setToken] = useState(null);
  const [generatingToken, setGeneratingToken] = useState(false);

  useEffect(() => {
    checkAccess();
    fetchDevices();
  }, []);

  const checkAccess = async () => {
    try {
      const authToken = localStorage.getItem('token');
      const societeId = localStorage.getItem('societeId');
      
      const response = await fetch(`${API_URL}/api/options/check/desktop_agent`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'X-Societe-Id': societeId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccessStatus(data);
      }
    } catch (error) {
      console.error('Erreur vérification accès:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDevices = async () => {
    try {
      const authToken = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/desktop-agent/devices`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDevices(data.devices || []);
      }
    } catch (error) {
      console.error('Erreur récupération appareils:', error);
    }
  };

  const generateToken = async () => {
    setGeneratingToken(true);
    try {
      const authToken = localStorage.getItem('token');
      const societeId = localStorage.getItem('societeId');
      
      const response = await fetch(`${API_URL}/api/desktop-agent/generate-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'X-Societe-Id': societeId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ device_name: 'Mon PC' })
      });
      
      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
      }
    } catch (error) {
      console.error('Erreur génération token:', error);
    } finally {
      setGeneratingToken(false);
    }
  };

  const startTrial = async () => {
    try {
      const authToken = localStorage.getItem('token');
      const societeId = localStorage.getItem('societeId');
      
      const response = await fetch(`${API_URL}/api/options/essai/desktop_agent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'X-Societe-Id': societeId
        }
      });
      
      if (response.ok) {
        checkAccess();
      }
    } catch (error) {
      console.error('Erreur démarrage essai:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const hasAccess = accessStatus?.has_access;

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="agent-desktop-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl text-white">
          <Monitor className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent Desktop IA</h1>
          <p className="text-gray-500">Contrôlez votre PC à distance via l'assistant Bureau Agricole</p>
        </div>
      </div>

      {/* Status Card */}
      <div className={`rounded-xl p-6 mb-8 ${hasAccess ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
        <div className="flex items-center gap-3 mb-4">
          {hasAccess ? (
            <>
              <CheckCircle className="w-6 h-6 text-emerald-600" />
              <span className="font-semibold text-emerald-800">
                {accessStatus?.reason === 'owner_access' ? 'Accès Propriétaire' : 
                 accessStatus?.essai ? 'Essai Gratuit Actif' : 'Abonnement Actif'}
              </span>
            </>
          ) : (
            <>
              <XCircle className="w-6 h-6 text-amber-600" />
              <span className="font-semibold text-amber-800">Fonctionnalité Premium</span>
            </>
          )}
        </div>

        {hasAccess ? (
          <p className="text-emerald-700">
            Vous avez accès à l'Agent Desktop. Téléchargez et installez l'agent sur votre PC pour commencer.
          </p>
        ) : (
          <div>
            <p className="text-amber-700 mb-4">
              L'Agent Desktop est une option premium. Essayez gratuitement pendant 7 jours !
            </p>
            <div className="flex gap-3">
              <Button onClick={startTrial} className="bg-emerald-600 hover:bg-emerald-700">
                <Gift className="w-4 h-4 mr-2" />
                Essai Gratuit 7 jours
              </Button>
              <Button variant="outline">
                <CreditCard className="w-4 h-4 mr-2" />
                S'abonner - 39€/mois
              </Button>
            </div>
          </div>
        )}
      </div>

      {hasAccess && (
        <>
          {/* Installation */}
          <div className="bg-white rounded-xl border p-6 mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Download className="w-5 h-5 text-emerald-600" />
              Installation
            </h2>

            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-bold">1</div>
                <div>
                  <p className="font-medium">Téléchargez l'Agent Desktop</p>
                  <p className="text-sm text-gray-500 mb-2">Archive ZIP contenant le script et les instructions</p>
                  <a 
                    href={`${API_URL}/api/desktop-agent/download`}
                    download="BureauAgricoleAgent.zip"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Télécharger BureauAgricoleAgent.zip
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-bold">2</div>
                <div className="flex-1">
                  <p className="font-medium">Générez votre Token de connexion</p>
                  <p className="text-sm text-gray-500 mb-2">Ce token permet de lier votre PC à votre compte</p>
                  
                  {token ? (
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-gray-100 rounded text-sm font-mono">
                        {token}
                      </code>
                      <Button variant="outline" size="sm" onClick={() => copyToClipboard(token)}>
                        Copier
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={generateToken}
                      disabled={generatingToken}
                    >
                      <Key className="w-4 h-4 mr-2" />
                      {generatingToken ? 'Génération...' : 'Générer Token'}
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-bold">3</div>
                <div>
                  <p className="font-medium">Lancez l'Agent et collez le Token</p>
                  <p className="text-sm text-gray-500">L'agent se connectera automatiquement à Bureau Agricole</p>
                </div>
              </div>
            </div>
          </div>

          {/* Appareils connectés */}
          <div className="bg-white rounded-xl border p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Monitor className="w-5 h-5 text-emerald-600" />
                Appareils connectés
              </h2>
              <Button variant="ghost" size="sm" onClick={fetchDevices}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {devices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Monitor className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun appareil connecté</p>
                <p className="text-sm">Installez l'agent sur votre PC pour commencer</p>
              </div>
            ) : (
              <div className="space-y-3">
                {devices.map((device, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
                      <div>
                        <p className="font-medium">{device.device_id}</p>
                        <p className="text-sm text-gray-500">
                          {device.system_info?.os} - {device.system_info?.hostname}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-emerald-600 font-medium">Connecté</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Fonctionnalités */}
      <div className="bg-white rounded-xl border p-6">
        <h2 className="text-lg font-semibold mb-4">Ce que peut faire l'Agent Desktop</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-3">
            <Globe className="w-5 h-5 text-blue-500 mt-0.5" />
            <div>
              <p className="font-medium">Navigation Web</p>
              <p className="text-sm text-gray-500">"Va sur Leboncoin", "Ouvre la MSA"</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3">
            <Terminal className="w-5 h-5 text-purple-500 mt-0.5" />
            <div>
              <p className="font-medium">Applications</p>
              <p className="text-sm text-gray-500">"Ouvre Excel", "Lance Chrome"</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3">
            <FileSearch className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <p className="font-medium">Recherche de Fichiers</p>
              <p className="text-sm text-gray-500">"Trouve le contrat Dupont"</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3">
            <MousePointer className="w-5 h-5 text-emerald-500 mt-0.5" />
            <div>
              <p className="font-medium">Automatisation</p>
              <p className="text-sm text-gray-500">Clics, saisie de texte, raccourcis</p>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-red-50 rounded-lg">
          <div className="flex items-center gap-2 text-red-700">
            <Shield className="w-5 h-5" />
            <span className="font-medium">Sécurité</span>
          </div>
          <p className="text-sm text-red-600 mt-1">
            Arrêt d'urgence : déplacez la souris dans le coin supérieur gauche de l'écran
          </p>
        </div>
      </div>
    </div>
  );
}
